import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

interface AdminGuardProps {
  children: ReactNode;
}

const AdminGuard = ({ children }: AdminGuardProps) => {
  const { user, loading, isAdmin, checkingRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !checkingRole) {
      if (!user) {
        console.warn("[AdminGuard] Unauthorized access attempt: No user logged in");
        navigate("/", { replace: true });
        return;
      }

      if (!isAdmin) {
        console.warn("[AdminGuard] Unauthorized access attempt: User is not admin", {
          userId: user.id,
          email: user.email,
        });
        navigate("/", { replace: true });
        return;
      }
    }
  }, [user, loading, isAdmin, checkingRole, navigate]);

  // Show loading while checking auth and role
  if (loading || checkingRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verifierar behörighet...</p>
        </div>
      </div>
    );
  }

  // Don't render children if not admin
  if (!user || !isAdmin) {
    return null;
  }

  return <>{children}</>;
};

export default AdminGuard;
